import { useSyncExternalStore } from "react";

export const THEME_KEY = "slantscan_theme";

/**
 * One theme for the whole app, held outside React.
 *
 * A plain `useState` hook would give every component that called it a separate
 * copy: the toggle in the nav would flip its own state and nothing else would
 * re-render. The class on <html> is the real source of truth for CSS, and this
 * store is the source of truth for the handful of places that need the value in
 * JavaScript (canvas colours, web components) rather than in a stylesheet.
 */

const listeners = new Set();

// jsdom (and any other non-browser host) has no matchMedia. Nothing here should
// throw at import time just because it can't ask about the OS preference.
const prefersDark = () =>
  typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

const systemTheme = () => (prefersDark()?.matches ? "dark" : "light");

/** The explicit choice, or null while the visitor hasn't made one. */
function storedTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    // Safari in private mode throws on localStorage. Falling back to the OS
    // preference is a better failure than a blank page.
    return null;
  }
}

let theme = storedTheme() ?? systemTheme();

function apply(next) {
  theme = next;
  document.documentElement.classList.toggle("dark", next === "dark");
  listeners.forEach((fn) => fn());
}

// The pre-paint script in index.html has already set the class; this keeps the
// two in agreement if that script is ever removed or fails.
apply(theme);

export function setTheme(next) {
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    /* choice won't survive a reload, but it still applies now */
  }
  apply(next);
}

export const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

// Follow the OS until the visitor picks a side of their own. Once they click
// the toggle, storedTheme() is set and this stops applying.
prefersDark()?.addEventListener("change", (e) => {
  if (!storedTheme()) apply(e.matches ? "dark" : "light");
});

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export function useTheme() {
  return useSyncExternalStore(subscribe, () => theme, () => "light");
}

/**
 * A colour token resolved to a concrete `rgb(...)` string.
 *
 * Most of the app should use the Tailwind token classes (`text-ink`,
 * `bg-surface`) and never call this. It exists for the few props that are not
 * CSS — a canvas fill, a web component's attribute — where `var(--c-ink)` would
 * be handed over as an uninterpreted string.
 */
export function useThemeToken(name, fallback = "#0F172A") {
  useTheme(); // re-resolve when the theme flips
  const v =
    typeof window === "undefined"
      ? ""
      : getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `rgb(${v})` : fallback;
}
